import { forwardRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "./ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface Props {
  className?: string;
  /**
   * When `true`, render as a menu-item-style row (used inside the AppShell
   * dropdown). When `false` (default), render as a standard outlined Button.
   */
  asMenuItem?: boolean;
  children?: ReactNode;
  /**
   * Gate signing out behind the app-wide unsaved-changes guard — cancelling
   * must leave the user signed in. Defaults to a no-op that always allows
   * sign-out, so this component works standalone outside AppShell.
   */
  confirmNavigate?: () => boolean;
}

export const LogoutButton = forwardRef<HTMLButtonElement, Props>(
  function LogoutButton(
    { className, asMenuItem = false, children, confirmNavigate = () => true },
    ref,
  ) {
    const { signOut } = useAuth();
    const navigate = useNavigate();
    const [pending, setPending] = useState(false);

    const handleClick = async () => {
      if (!confirmNavigate()) return;
      setPending(true);
      try {
        await signOut();
        navigate("/login", { replace: true });
      } finally {
        setPending(false);
      }
    };

    if (asMenuItem) {
      return (
        <button
          ref={ref}
          type="button"
          disabled={pending}
          onClick={handleClick}
          className={cn(
            "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
            className,
          )}
        >
          {children ?? (pending ? "Signing out…" : "Sign out")}
        </button>
      );
    }

    return (
      <Button
        ref={ref}
        type="button"
        variant="outline"
        size="sm"
        disabled={pending}
        onClick={handleClick}
        className={className}
      >
        {pending ? "Signing out…" : "Sign out"}
      </Button>
    );
  },
);
